import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Instagram, MapPin } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT } from "@/lib/assets";

const ease = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  "Cosmetic Refresh",
  "Kitchen Renovation",
  "Bathroom Renovation",
  "Full Home Renovation",
  "Not sure yet",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry has been sent. I'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email me directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-border focus:border-ink outline-none py-3 text-ink placeholder:text-taupe/80 font-light transition-colors";

  return (
    <div data-testid="contact-page" className="bg-cream">
      {/* Hero */}
      <section className="pt-36 sm:pt-44 pb-14">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="max-w-3xl"
          >
            <Overline>Contact</Overline>
            <h1 className="font-serif font-light text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-6">
              Let's start the conversation.
            </h1>
            <p className="mt-7 text-muted-foreground text-lg font-light leading-relaxed max-w-2xl">
              The earlier we connect, the more thoughtfully we can plan. Whether your renovation is
              months away or already taking shape, I'd love to hear what you have in mind.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-28 sm:pb-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-taupe">Name *</label>
                  <input
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-taupe">Email *</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-taupe">Phone</label>
                  <input
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-taupe">Project type</label>
                  <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
                    <SelectTrigger
                      data-testid="contact-projecttype-trigger"
                      className="w-full bg-transparent border-0 border-b border-border rounded-none px-0 py-3 h-auto text-ink font-light focus:ring-0 focus:border-ink shadow-none"
                    >
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectTypes.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`projecttype-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-taupe">Tell me about your project *</label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="A little about your home, your vision and timing..."
                  className={`${inputClass} resize-none`}
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="bg-ink text-cream px-10 py-4 text-xs uppercase tracking-[0.18em] hover:bg-ink/85 transition-colors disabled:opacity-50"
              >
                {submitting ? "Sending..." : "Send enquiry"}
              </button>
            </form>
          </Reveal>

          {/* Details */}
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="bg-sand p-8 sm:p-10 border border-border">
              <Overline>Get in touch directly</Overline>
              <ul className="mt-8 space-y-7">
                <li className="flex items-start gap-4">
                  <Phone size={18} strokeWidth={1.3} className="text-ink/70 mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-taupe">Phone</p>
                    <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone-link" className="text-ink font-light hover:underline">
                      {CONTACT.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail size={18} strokeWidth={1.3} className="text-ink/70 mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-taupe">Email</p>
                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="text-ink font-light hover:underline break-all">
                      {CONTACT.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Instagram size={18} strokeWidth={1.3} className="text-ink/70 mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-taupe">Instagram</p>
                    <a href={CONTACT.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram-link" className="text-ink font-light hover:underline">
                      {CONTACT.instagram}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={18} strokeWidth={1.3} className="text-ink/70 mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-taupe">Location</p>
                    <p className="text-ink font-light">{CONTACT.location}</p>
                  </div>
                </li>
              </ul>
              <p className="mt-10 text-sm text-muted-foreground font-light leading-relaxed border-t border-border pt-6">
                I work with homeowners who want a calm, well-managed renovation experience. Reach out
                early — it's the best way to ensure every detail is considered from the start.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
